import React, { createContext, useContext, ReactNode } from 'react'
import { Map } from 'immutable'

export type InternationalStrings = Map<string, unknown>

/**
 * Utility function to fetch internationalized text using a lookup path.
 *
 * const result = i18nText(['messageGreeting'], testStrings, {
 *     name: 'Bob',
 *     count: '5'
 * }) // ->  'Hello Bob, you have 5 messages'
 *
 * @param keys - Array of keys forming the lookup path (e.g., ['sidebarItem', 'select'])
 * @param lookup - InternationalStrings map containing the translations
 * @param params - Optional parameters for string interpolation (e.g., {name: 'John'})
 * @returns Translated string with parameters interpolated, or the last key as fallback
 */
const i18nText = (
  keys: string[],
  lookup: InternationalStrings | undefined,
  params: Record<string, string> = {},
): string => {
  if (!lookup || !lookup.getIn || keys.length === 0) {
    // eslint-disable-next-line no-console
    console.warn('i18nText could not find value for key path:', keys)
    return keys[keys.length - 1] || ''
  }

  const template = lookup.getIn(keys, keys[keys.length - 1]) as string

  // Replace {paramName} patterns with actual values
  return template.replace(/{(\w+)}/g, (match: string, param: string) => {
    return params[param] || match
  })
}

interface StaticTextContextValue {
  staticText: InternationalStrings | undefined
}

const StaticTextContext = createContext<StaticTextContextValue | undefined>(undefined)

interface StaticTextProviderProps {
  children: ReactNode
  staticText: InternationalStrings | undefined
}

export const StaticTextProvider: React.FC<StaticTextProviderProps> = ({
  children,
  staticText,
}) => {
  return (
    <StaticTextContext.Provider value={{ staticText }}>
      {children}
    </StaticTextContext.Provider>
  )
}

export const useTranslatedText = (
  { keys, params = {} }: { keys: string[], params?: Record<string, string> },
) => {
  const context = useContext(StaticTextContext)

  if (context === undefined) {
    throw new Error('useStaticText must be used within a StaticTextProvider')
  }

  return i18nText(['data', ...keys], context.staticText, params)
}
